/**
 * Fetch scorecard templates for the template selector in ScorecardStep.
 * GET /api_ats/v1/ats_scorecards/list_templates
 * Supports optional job-context filtering by department/office/employment_type.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { http } from '@/lib/ats-https';
import { asAtsClientBody } from '@/api/ats/atsEnvelope';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { getAtsJobScorecardParams } from '@/store/atsJobDetails';
import { useInterviewStageManager } from '@/context-providers/interviewStageManager';
import { buildScorecardTemplateParams } from './buildScorecardTemplateParams';
import {
  readJobWorkTypeId,
  useWorkTypeOptions,
} from '@/api/work-types';

export interface ScorecardTemplate {
  name: string;
  questions?: Array<{
    type?: string;
    question?: string;
    description?: string;
    mandatory?: boolean;
    options?: string[];
  }>;
  feedback_description?: string;
  is_feedback_mandatory?: boolean;
  rating_description?: string;
}

interface SelectOption {
  value: string;
  label: string;
}

async function fetchScorecardTemplates(
  params?: string
): Promise<ScorecardTemplate[]> {
  const url = params
    ? `ats_scorecards/list_templates?${params}`
    : 'ats_scorecards/list_templates';
  const raw = asAtsClientBody<{
    data?: { ats_scorecards?: ScorecardTemplate[] };
    ats_scorecards?: ScorecardTemplate[];
  }>(await http.get(url));
  return raw?.data?.ats_scorecards ?? raw?.ats_scorecards ?? [];
}

export function useScorecardTemplates() {
  // Read job context for filtering templates by department/office/employment_type
  const { currentJob } = useSelector(getAtsJobCreate) as {
    currentJob?: {
      isLoading?: boolean;
      data?: {
        basic_info?: {
          department?: { id?: number };
          office?: Array<{ id?: number }>;
          employment_type?: string;
          ats_work_type?: { id?: number };
          ats_work_type_id?: number;
        };
      };
    };
  };

  // The creation slice is only populated inside the job-creation wizard. On the
  // job's own settings the scope has to come from the job-details slice, or the
  // request goes out unfiltered and department-scoped scorecards never appear
  // in the picker (ENG-3790).
  const jobDetailsScope = useSelector(getAtsJobScorecardParams) as
    | {
        department_ids?: unknown;
        office_ids?: unknown;
        employment_types?: unknown;
        ats_work_type_ids?: unknown;
      }
    | undefined;

  // A settings interview plan belongs to no job at all — its scope is the
  // plan's own entity scope, handed down through the stage manager.
  const { scorecardParams: planScope } = useInterviewStageManager() as {
    scorecardParams?: {
      department_ids?: unknown;
      office_ids?: unknown;
      employment_types?: unknown;
      ats_work_type_ids?: unknown;
    };
  };

  const { isWorkTypesEnabled } = useWorkTypeOptions();
  // Read outside the memo so the dependency is a plain id.
  const creationWorkTypeId = readJobWorkTypeId(
    currentJob?.data?.basic_info ?? {}
  );

  const queryParams = useMemo(() => {
    const basicInfo = currentJob?.data?.basic_info;
    // Flag ON scope by work type id; OFF by the legacy enum. Every source
    // below picks one side or the other, never both.
    const scopeWorkType = (atsWorkTypeIds: unknown, employmentTypes: unknown) =>
      isWorkTypesEnabled ? { atsWorkTypeIds } : { employmentTypes };

    const fromCreation = buildScorecardTemplateParams({
      departmentIds: basicInfo?.department?.id
        ? [basicInfo.department.id]
        : undefined,
      officeIds: basicInfo?.office?.map((o) => o.id),
      ...scopeWorkType(
        [creationWorkTypeId],
        basicInfo?.employment_type ? [basicInfo.employment_type] : undefined
      ),
    });
    if (fromCreation) return fromCreation;

    const fromJobDetails = buildScorecardTemplateParams({
      departmentIds: jobDetailsScope?.department_ids,
      officeIds: jobDetailsScope?.office_ids,
      ...scopeWorkType(
        jobDetailsScope?.ats_work_type_ids,
        jobDetailsScope?.employment_types
      ),
    });
    if (fromJobDetails) return fromJobDetails;

    return buildScorecardTemplateParams({
      departmentIds: planScope?.department_ids,
      officeIds: planScope?.office_ids,
      ...scopeWorkType(planScope?.ats_work_type_ids, planScope?.employment_types),
    });
  }, [
    currentJob,
    jobDetailsScope,
    planScope,
    isWorkTypesEnabled,
    creationWorkTypeId,
  ]);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['scorecard-templates', queryParams],
    queryFn: () => fetchScorecardTemplates(queryParams),
    staleTime: 5 * 60 * 1000,
  });

  const templateOptions = useMemo(
    (): SelectOption[] =>
      templates.map((t) => ({
        value: t.name,
        label: t.name,
      })),
    [templates]
  );

  return { templates, templateOptions, isLoading };
}
